import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import book_appointment from "../../../../assets/images/icons/calendar.svg";
import menu from "../../../../assets/images/icons/menu.svg";
import Ic_pdf from "../../../../assets/images/icons/pdf.svg";
import add_prescription from "../../../../assets/images/icons/prescription.svg";
import report_patient from "../../../../assets/images/icons/Report.svg";
import { Dropdown, ProfileAvatar } from "../../../../components";
import { EndPoints } from "../../../../Constants/endpoints";
import { useGetData, useGetPersonalInfo, usePrintPdf } from "../../../../hooks";
import { RootState } from "../../../../redux/store";
import { useToast } from "../../../../Services/ToastContext";
import { highlightSearchString } from "../../../../utils/highlightSearchString";
import { formatAge, getGenderEmoji } from "../../../../utils/utils";
import EditPatient from "../addPatient/EditPatient";
import AddPrescriptionModal from "./AddPrescriptionModal";

interface PatientCardProps {
  to?: string;
  noOptions?: boolean;
  isPending?: boolean;
  bookAppointmentClick?: () => void;
  reportPatientClick?: () => void;
  item: any;
}

const PatientCard: React.FC<PatientCardProps> = ({
  isPending,
  bookAppointmentClick,
  reportPatientClick,
  to,
  noOptions,
  item,
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isModalPrescriptionOpen, setIsPrescriptionModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { data, isLoading, isSuccess, isError } = useGetData<any>(
    EndPoints.PatientsReports,
    {
      filter: {
        PatientId: item?.PatientId,
      },
      enabled: isFetching,
    },
  );

  const { isAllowed } = useGetPersonalInfo();
  const { showToast } = useToast();

  const { t } = useTranslation();

  const location = useLocation();

  const handleBack = () => {
    navigate(location.state?.from || -1);
  };

  const { handleExportPDF } = usePrintPdf({
    enabled: false,
    file: data,
  });

  useEffect(() => {
    if (isSuccess && isFetching) {
      handleExportPDF()
        .then(() => {
          showToast(t("file_downloaded_successfully"), "success");
          setIsFetching(false);
        })
        .catch(() => {
          setIsFetching(false);
        });
    }
    if (isError && isFetching) {
      showToast(t("error"), "error");
      setIsFetching(false);
    }
  }, [isSuccess, isError, isFetching]);

  const operations: any[] = [
    ...(isAllowed
      ? [
          {
            icon: book_appointment,
            label: t("book_appointment"),
            onClick: bookAppointmentClick,
            className: `w-60 !text-body-text items-center gap-2 rounded-md py-3 px-2`,
          },
          {
            icon: add_prescription,
            label: t("add_prescription"),
            onClick: () => {
              setIsPrescriptionModalOpen(true);
            },
            className: `items-center !text-body-text gap-2 rounded-md py-3 px-2`,
          },
       !item?.Patient?.HasMedicalRecord
            ? {
                icon: Ic_pdf,
                label: t("add_medical_report"),
                className: `items-center !text-body-text gap-2 rounded-md py-3 px-2`,
                onClick: () => {
                  item?.PatientId
                    ? navigate(`/patient/${item?.PatientId}/add-medical-record`)
                    : handleBack();
                },
              }
            : null,
        ]
      : []),
    // item?.Patient?.CreatedByDoctor
    //   ? {
    //       icon: edit,
    //       label: t("edit_Patient_name"),
    //       onClick: () => {
    //         setIsEditModalOpen(true);
    //       },
    //       className: `items-center !text-body-text gap-2 rounded-md py-3 px-2`,
    //     }
    //   : null,
    {
      icon: Ic_pdf,
      label: t("export_patient_report"),
      className: `items-center !text-body-text gap-2 rounded-md py-3 px-2`,
      onClick: () => {
        !isLoading && setIsFetching(true);
      },
    },

     item?.Patient?.HasMedicalRecord
      ? {
          icon: Ic_pdf,
          label: t("view_medical_records"),
          className: `items-center !text-body-text gap-2 rounded-md py-3 px-2 `,
          onClick: () => {
            navigate(`/patients_list/${item?.PatientId}/medical-records`);
          },
        }
      : null,
    // {
    //   icon: remove,
    //   label: t("remove"),
    //   onClick: () => setIsDeleteModalOpen(true),
    //   className: `items-center !text-base !text-error !gap-2 rounded-md py-3 px-2 mt-2 w-full rounded-t-none border-t-2 border-InputFieldColor`,
    // },
    {
      icon: report_patient,
      label: t("report_patient"),
      onClick: reportPatientClick,
      className: `items-center !text-error gap-2 rounded-md py-3 px-2`,
    },
  ]?.filter(Boolean);

  const currentLocationParams = new URLSearchParams(location.search);
  const searchValue = currentLocationParams.get("search-value") ?? "";
  const highlightedText = highlightSearchString(
    item?.Patient.FullName,
    searchValue,
  );

  const UserInfo = useSelector((state: RootState) => state.registration);

  return (
    <>
      <div className="secondary_warper flex w-full items-center justify-between lg:min-w-96">
        {to ? (
          <Link to={to}>
            <div className="flex cursor-pointer items-center gap-2">
              <ProfileAvatar
                src={item?.Patient.Image}
                Id={Number(item?.Patient?.Id)}
                FullName={item?.Patient.FullName}
                alt={item?.Patient.FullName}
                isLoad={isPending}
                className={`h-12 w-12 rounded-full ${item?.Patient.Image ? "!object-cover" : ""}`}
              />
              <div className="flex flex-col gap-1 pt-2">
                <div
                  className="font-bold leading-tight text-primary-text"
                  dangerouslySetInnerHTML={{ __html: highlightedText }}
                />
                <p className="text-sm text-body-text">
                  {item?.Patient.Birthday &&
                    formatAge(item?.Patient.Birthday, true) +
                      " " +
                      t("years_old") +
                      " "}
                  {item?.Patient.Gender &&
                    t(item?.Patient.Gender.trim().toLowerCase()) &&
                    " • " + item?.Patient.Gender &&
                    t(item?.Patient.Gender.trim().toLowerCase()) + " • "}
                  <span dir="ltr">
                    {item?.Patient.PhoneNumber &&
                      UserInfo.userDetails.Country?.Code +
                        "-" +
                        item?.Patient.PhoneNumber}
                  </span>
                </p>
              </div>
            </div>
          </Link>
        ) : (
          <div className="flex cursor-pointer gap-2 lg:p-2">
            <ProfileAvatar
              src={item?.Patient.Image}
              Id={item?.Patient?.Id}
              FullName={item?.Patient.FullName}
              alt={item?.Patient.FullName}
              isLoad={isPending}
              className={`h-12 w-12 rounded-full ${item?.Patient.Image ? "!object-cover" : ""}`}
            />
            <div className="flex flex-col gap-1 pt-2">
              <div
                className="font-bold leading-tight text-primary-text"
                dangerouslySetInnerHTML={{ __html: highlightedText }}
              />
              <p className="text-body-text">
                {item?.Patient.Birthday &&
                  formatAge(item?.Patient.Birthday) +
                    " " +
                    t("years_old") +
                    " "}
                {item?.Patient.Gender &&
                  t(item?.Patient.Gender.trim().toLowerCase()) &&
                  " • " + item?.Patient.Gender &&
                  t(item?.Patient.Gender.trim().toLowerCase()) +
                    getGenderEmoji(
                      item?.Patient.Gender &&
                        t(item?.Patient.Gender.trim().toLowerCase()),
                    ) +
                    " • "}
                {item?.Patient.PhoneNumber && item?.Patient.PhoneNumber}
              </p>
            </div>
          </div>
        )}
        {noOptions ? (
          ""
        ) : (
          <Dropdown
            menuItems={operations}
            children={
              <img
                src={menu}
                alt="menu"
                className="h-[24px] w-[24px] cursor-pointer transition-all hover:rounded-full hover:bg-blue-50"
                onClick={() => setIsOpen(!isOpen)}
              />
            }
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />
        )}
      </div>

      {isModalPrescriptionOpen && (
        <AddPrescriptionModal
          isOpen={isModalPrescriptionOpen}
          onClose={() => setIsPrescriptionModalOpen(false)}
          patientData={item?.Patient}
        />
      )}
      {isEditModalOpen && (
        <EditPatient
          onClose={() => setIsEditModalOpen(false)}
          isAddModalOpen={isEditModalOpen}
          patientData={item?.Patient}
        />
      )}
      {/* {isDeleteModalOpen && (
        <RemovePatient
          name={item?.Patient?.FullName}
          id={item?.PatientId}
          closeDeleteModal={() => setIsDeleteModalOpen(false)}
          isDeleteModalOpen={isDeleteModalOpen}
        />
      )} */}
    </>
  );
};

export default PatientCard;
